import { useEffect, useState } from 'react';
import {
  FileText,
  FolderKanban,
  Lightbulb,
  Lock,
  Sparkles,
  UploadCloud,
  type LucideIcon,
} from 'lucide-react';

import SearchBar, { type Member } from '../../ui/SearchBar/SearchBar';
import Dropdown, { type DropdownOption } from '../../ui/Dropdown/Dropdown';
import InputField from '../../ui/InputField/InputField';
import TextArea from '../../ui/TextArea/TextArea';
import FileDrop from '../../ui/FileDrop/FileDrop';
import {
  AlertDialog,
  type AlertDialogType,
} from '../../ui/AlertDialog/AlertDialog';
import { courseService } from '../../../services/courseService';

import './ProjectForm.css';

export interface ProjectFormData {
  course: string;
  projectName: string;
  projectType: string;
  aiModel: 'gpt' | 'custom_hf';
  description: string;
  text: string;
}

interface ProjectFormProps {
  initialData?: ProjectFormData;
  selectedTeamMembers?: Member[];
  selectedFiles?: File[];
  isSubmitting?: boolean;
  isEditing?: boolean;
  hasSubmissions?: boolean;
  projectId?: string;
  onSubmit: (
    data: ProjectFormData,
    teamMembers: Member[],
    files: File[],
  ) => void;
  onCancel: () => void;
}

interface SectionHeadingProps {
  icon: LucideIcon;
  optionalLabel?: string;
  title: string;
}

const projectTypeOptions: DropdownOption[] = [
  { id: 'group', label: 'Group Project' },
  { id: 'individual', label: 'Individual Project' },
];

const aiModelOptions: DropdownOption[] = [
  { id: 'gpt', label: 'GPT Model' },
  { id: 'custom_hf', label: 'Custom Model' },
];

const memberColorOptions: Array<
  'blue' | 'pink' | 'green' | 'purple' | 'teal' | 'yellow'
> = ['blue', 'pink', 'green', 'purple', 'teal', 'yellow'];

const individualProjectOption =
  projectTypeOptions.find((option) => option.id === 'individual') ?? null;

const SectionHeading = ({
  icon: Icon,
  optionalLabel,
  title,
}: SectionHeadingProps) => (
  <div className="project-form__section-heading">
    <Icon size={18} strokeWidth={2.1} />
    <h2 className="project-form__section-title">
      {title}
      {optionalLabel ? (
        <span className="project-form__section-optional">{optionalLabel}</span>
      ) : null}
    </h2>
  </div>
);

const buildMemberColor = (
  value: string,
): 'blue' | 'pink' | 'green' | 'purple' | 'teal' | 'yellow' => {
  let hash = 0;

  for (const character of value) {
    hash = (hash * 31 + character.charCodeAt(0)) >>> 0;
  }

  return memberColorOptions[hash % memberColorOptions.length];
};

const ProjectForm = ({
  initialData,
  selectedTeamMembers: initialTeamMembers = [],
  selectedFiles: initialFiles = [],
  isSubmitting = false,
  isEditing = false,
  hasSubmissions = false,
  projectId,
  onSubmit,
  onCancel,
}: ProjectFormProps) => {
  const [formData, setFormData] = useState<ProjectFormData>(
    initialData || {
      course: '',
      projectName: '',
      projectType: '',
      aiModel: 'gpt',
      description: '',
      text: '',
    },
  );

  const [selectedTeamMembers, setSelectedTeamMembers] =
    useState<Member[]>(initialTeamMembers);
  const [selectedFiles, setSelectedFiles] = useState<File[]>(initialFiles);
  const [availableMembers, setAvailableMembers] = useState<Member[]>([]);
  const [courseOptions, setCourseOptions] = useState<DropdownOption[]>([]);
  const [selectedCourse, setSelectedCourse] = useState<DropdownOption | null>(
    null,
  );
  const [selectedProjectType, setSelectedProjectType] =
    useState<DropdownOption | null>(
      initialData?.projectType
        ? projectTypeOptions.find((option) => option.id === initialData.projectType) ??
            null
        : null,
    );
  const [selectedAiModel, setSelectedAiModel] = useState<DropdownOption | null>(
    aiModelOptions.find((option) => option.id === formData.aiModel) ?? null,
  );

  const [isLoadingCourses, setIsLoadingCourses] = useState(true);
  const [isLoadingMembers, setIsLoadingMembers] = useState(false);
  const [enrolledStudentsFetchError, setEnrolledStudentsFetchError] =
    useState(false);

  const [isAlertOpen, setIsAlertOpen] = useState(false);
  const [alertType, setAlertType] = useState<AlertDialogType>('info');
  const [alertTitle, setAlertTitle] = useState('Review your submission');
  const [primaryButtonText, setPrimaryButtonText] = useState('Confirm');
  const [alertMsg, setAlertMsg] = useState('');

  useEffect(() => {
    const fetchEnrolledCourses = async () => {
      try {
        setIsLoadingCourses(true);
        const courses = await courseService.getEnrolledCourses();
        const options: DropdownOption[] = courses.map((course) => ({
          id: course.id,
          label: course.code ? `${course.code} - ${course.title}` : course.title,
        }));

        setCourseOptions(options);

        if (initialData?.course) {
          const initialCourse =
            options.find((course) => course.id === initialData.course) ?? null;

          if (initialCourse) {
            setSelectedCourse(initialCourse);
          }
        }
      } catch (error) {
        console.error('Failed to fetch enrolled courses:', error);
        setAlertType('error');
        setAlertTitle('Error');
        setAlertMsg(
          'Failed to load your enrolled courses. Please refresh the page.',
        );
        setPrimaryButtonText('Close');
        setIsAlertOpen(true);
      } finally {
        setIsLoadingCourses(false);
      }
    };

    void fetchEnrolledCourses();
  }, [initialData]);

  useEffect(() => {
    const fetchEnrolledStudents = async () => {
      if (!selectedCourse) {
        setAvailableMembers([]);
        setEnrolledStudentsFetchError(false);
        return;
      }

      try {
        setIsLoadingMembers(true);
        setEnrolledStudentsFetchError(false);
        const students = await courseService.getEnrolledStudents(selectedCourse.id);
        const members: Member[] = students.map((student) => {
          const nameParts = student.display_name.split(' ').filter(Boolean);
          const initials = nameParts
            .map((part) => part.charAt(0).toUpperCase())
            .join('')
            .slice(0, 2);

          return {
            id: student.id,
            name: student.display_name,
            initials,
            backgroundColor: buildMemberColor(student.id),
          };
        });

        setAvailableMembers(members);
      } catch (error) {
        console.error('Failed to fetch enrolled students:', error);
        setAvailableMembers([]);
        setEnrolledStudentsFetchError(true);
      } finally {
        setIsLoadingMembers(false);
      }
    };

    void fetchEnrolledStudents();
  }, [selectedCourse]);

  useEffect(() => {
    if (selectedProjectType?.id !== 'group' && selectedTeamMembers.length > 0) {
      setSelectedTeamMembers([]);
    }
  }, [selectedProjectType, selectedTeamMembers.length]);

  useEffect(() => {
    if (
      !isEditing &&
      selectedCourse &&
      !isLoadingMembers &&
      availableMembers.length < 1 &&
      selectedProjectType?.id === 'group'
    ) {
      setSelectedProjectType(individualProjectOption);
    }
  }, [
    availableMembers.length,
    isEditing,
    isLoadingMembers,
    selectedCourse,
    selectedProjectType,
  ]);

  const handleInputChange = (
    field: keyof ProjectFormData,
    value: ProjectFormData[keyof ProjectFormData],
  ) => {
    setFormData((previousState) => ({
      ...previousState,
      [field]: value,
    }));
  };

  const handleCourseSelect = (option: DropdownOption) => {
    setSelectedCourse(option);

    if (!isEditing) {
      setSelectedProjectType(null);
      setSelectedTeamMembers([]);
    }
  };

  const handleFilesSelected = (files: File[]) => {
    setSelectedFiles((previousState) => [...previousState, ...files]);
  };

  const handleRemoveFile = (index: number) => {
    setSelectedFiles((previousState) =>
      previousState.filter((_, fileIndex) => fileIndex !== index),
    );
  };

  const buildSubmissionSummary = () => {
    const isPdfSubmission = selectedFiles.length > 0;
    const lines = [
      `Course: ${selectedCourse?.label || '—'}`,
      `Project name: ${formData.projectName || '—'}`,
      `Project type: ${selectedProjectType?.label || '—'}`,
      `AI model: ${selectedAiModel?.label || '—'}`,
    ];

    if (selectedTeamMembers.length > 0) {
      lines.push(
        `Team members: ${selectedTeamMembers
          .map((member) => member.name)
          .join(', ')}`,
      );
    }

    if (isPdfSubmission) {
      const totalKb = selectedFiles.reduce(
        (sum, file) => sum + file.size / 1024,
        0,
      );
      const fileNames = selectedFiles.map((file) => file.name).join(', ');
      lines.push(
        `Files (${selectedFiles.length}): ${fileNames} · ${totalKb.toFixed(1)} KB`,
      );
    } else {
      const essayLength = (formData.text || '').length;
      lines.push(`Essay text: ${essayLength.toLocaleString()} characters`);
    }

    return lines.join('\n');
  };

  const handleFormSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!formData.projectName.trim()) {
      setAlertType('error');
      setAlertTitle('Missing Project Name');
      setAlertMsg('Please enter a project name.');
      setPrimaryButtonText('Close');
      setIsAlertOpen(true);
      return;
    }

    if (!selectedCourse) {
      setAlertType('error');
      setAlertTitle('Missing Course');
      setAlertMsg('Please select a course.');
      setPrimaryButtonText('Close');
      setIsAlertOpen(true);
      return;
    }

    if (!selectedProjectType) {
      setAlertType('error');
      setAlertTitle('Missing Project Type');
      setAlertMsg('Please select a project type.');
      setPrimaryButtonText('Close');
      setIsAlertOpen(true);
      return;
    }

    if (!selectedAiModel) {
      setAlertType('error');
      setAlertTitle('Missing AI Model');
      setAlertMsg('Please select which model should evaluate this project.');
      setPrimaryButtonText('Close');
      setIsAlertOpen(true);
      return;
    }

    setAlertMsg(buildSubmissionSummary());
    setAlertTitle(isEditing ? 'Review your changes' : 'Review your submission');
    setPrimaryButtonText(isEditing ? 'Update' : 'Create');
    setAlertType('info');
    setIsAlertOpen(true);
  };

  const handleConfirmSubmit = () => {
    const isPdfSubmission = selectedFiles.length > 0;

    onSubmit(
      {
        ...formData,
        course: selectedCourse?.id || '',
        projectType: selectedProjectType?.id || '',
        aiModel: (selectedAiModel?.id || 'gpt') as 'gpt' | 'custom_hf',
        text: isPdfSubmission ? '' : formData.text,
      },
      selectedTeamMembers,
      selectedFiles,
    );
  };

  const isCourseLocked = isEditing && Boolean(selectedCourse || initialData?.course);
  const isProjectTypeLocked =
    isEditing && Boolean(selectedProjectType || initialData?.projectType);
  const isAiModelLocked = isEditing && Boolean(selectedAiModel || initialData?.aiModel);
  const shouldShowTeamMembers = selectedProjectType?.id === 'group';
  const isPdfSubmission = selectedFiles.length > 0;
  const gridClassName = isPdfSubmission
    ? 'project-form-page__grid project-form-page__grid--single'
    : 'project-form-page__grid';
  const projectTypeDropdownOptions =
    availableMembers.length < 1 && !isLoadingMembers
      ? individualProjectOption
        ? [individualProjectOption]
        : []
      : projectTypeOptions;

  return (
    <div className="project-form-page">
      <form onSubmit={handleFormSubmit} className="project-form-page__form">
        <div className="project-form-page__scroll">
          <div className="project-form-page__content">
            <header className="project-form-page__header">
              <h1 className="project-form-page__title heading-5">
                {isEditing ? 'Edit Project' : 'Create New Project'}
              </h1>
            </header>

            <div className={gridClassName}>
              <section className="project-form-page__card">
                <SectionHeading icon={FolderKanban} title="Project Details" />

                <div className="project-form__field">
                  <label className="project-form__label" htmlFor="project-course">
                    Course
                  </label>
                  {isLoadingCourses ? (
                    <div className="project-form__readonly">Loading courses...</div>
                  ) : courseOptions.length === 0 ? (
                    <div className="project-form__notice is-warning">
                      You are not enrolled in any courses yet. Please enrol in a
                      course first.
                    </div>
                  ) : isCourseLocked ? (
                    <div className="project-form__readonly">
                      {selectedCourse?.label || initialData?.course || '—'}
                    </div>
                  ) : (
                    <div id="project-course">
                      <Dropdown
                        options={courseOptions}
                        selectedOption={selectedCourse}
                        onSelect={handleCourseSelect}
                        placeholder="Select course"
                      />
                    </div>
                  )}
                </div>

                <div className="project-form__field">
                  <label className="project-form__label" htmlFor="project-name">
                    Project Name
                  </label>
                  <InputField
                    value={formData.projectName}
                    onChange={(value) => handleInputChange('projectName', value)}
                    placeholder="Enter project name"
                    id="project-name"
                  />
                </div>

                <div className="project-form__field">
                  <label className="project-form__label" htmlFor="project-type">
                    Project Type
                  </label>
                  {isProjectTypeLocked ? (
                    <div className="project-form__readonly">
                      {selectedProjectType?.label || initialData?.projectType || '—'}
                    </div>
                  ) : !selectedCourse ? (
                    <div className="project-form__readonly is-muted">
                      <Lock size={14} strokeWidth={2.2} />
                      <span>Please select a course first</span>
                    </div>
                  ) : (
                    <>
                      <div id="project-type">
                        <Dropdown
                          options={projectTypeDropdownOptions}
                          selectedOption={selectedProjectType}
                          onSelect={setSelectedProjectType}
                          placeholder="Select project type"
                        />
                      </div>
                      {availableMembers.length < 1 && !isLoadingMembers ? (
                        <p className="project-form__helper is-warning">
                          Only individual projects are available because there are no
                          other enrolled classmates in this course yet.
                        </p>
                      ) : null}
                    </>
                  )}
                </div>

                <div className="project-form__field">
                  <label className="project-form__label" htmlFor="project-model">
                    AI Model
                  </label>
                  {isAiModelLocked ? (
                    <div className="project-form__readonly">
                      {selectedAiModel?.label || initialData?.aiModel || '—'}
                    </div>
                  ) : (
                    <>
                      <div id="project-model">
                        <Dropdown
                          options={aiModelOptions}
                          selectedOption={selectedAiModel}
                          onSelect={setSelectedAiModel}
                          placeholder="Select evaluation model"
                        />
                      </div>
                      <p className="project-form__helper">
                        GPT keeps the current scoring flow. Custom Model scores
                        each rubric dimension with its own hosted Hugging Face
                        model.
                      </p>
                    </>
                  )}
                </div>

                <div className="project-form__field">
                  <label className="project-form__label" htmlFor="project-description">
                    Description
                  </label>
                  <TextArea
                    value={formData.description}
                    onChange={(value) => handleInputChange('description', value)}
                    placeholder="Enter project description"
                    rows={3}
                    maxLength={300}
                    showCharCount={true}
                    className="project-form__text-area"
                    id="project-description"
                  />
                </div>

                {shouldShowTeamMembers ? (
                  <div className="project-form__field">
                    <label className="project-form__label">Team Members</label>
                    {!selectedCourse ? (
                      <div className="project-form__notice">
                        Please select a course first to see available classmates.
                      </div>
                    ) : isLoadingMembers ? (
                      <div className="project-form__notice">Loading classmates...</div>
                    ) : enrolledStudentsFetchError ? (
                      <div className="project-form__notice is-warning">
                        Unable to load classmates right now. Please try again.
                      </div>
                    ) : availableMembers.length === 0 ? (
                      <div className="project-form__notice is-warning">
                        No other students are enrolled in this course yet.
                      </div>
                    ) : (
                      <SearchBar
                        placeholder="Search and select team members"
                        members={availableMembers.filter(
                          (member) =>
                            !selectedTeamMembers.some(
                              (selectedMember) => selectedMember.id === member.id,
                            ),
                        )}
                        onSelect={(member) => {
                          if (
                            !selectedTeamMembers.some(
                              (selectedMember) => selectedMember.id === member.id,
                            )
                          ) {
                            setSelectedTeamMembers((previousState) => [
                              ...previousState,
                              member,
                            ]);
                          }
                        }}
                        maxResults={6}
                        className="project-form__member-search"
                      />
                    )}

                    {selectedTeamMembers.length > 0 ? (
                      <div className="project-form__selection-list">
                        {selectedTeamMembers.map((member) => (
                          <div key={member.id} className="project-form__selection-row">
                            <div className="project-form__selection-copy">
                              <span className="project-form__selection-name">
                                {member.name}
                              </span>
                            </div>
                            <button
                              type="button"
                              className="project-form__selection-remove"
                              onClick={() =>
                                setSelectedTeamMembers((previousState) =>
                                  previousState.filter(
                                    (selectedMember) => selectedMember.id !== member.id,
                                  ),
                                )
                              }
                            >
                              Remove
                            </button>
                          </div>
                        ))}
                      </div>
                    ) : null}
                  </div>
                ) : null}

                {!isEditing ? (
                  <>
                    <div className="project-form__upload-block">
                      <div className="project-form__upload-heading">
                        <UploadCloud size={18} strokeWidth={2.1} />
                        <div>
                          <h3>Upload PDF File</h3>
                          <p>
                            Optional. Only text-based PDFs are supported for AI
                            evaluation.
                          </p>
                        </div>
                      </div>
                      <div className="project-form__upload-control">
                        <FileDrop
                          onFilesSelected={handleFilesSelected}
                          accept=".pdf"
                          multiple={true}
                          maxSize={50 * 1024 * 1024}
                        />
                      </div>
                    </div>

                    {selectedFiles.length > 0 ? (
                      <div className="project-form__selection-list">
                        {selectedFiles.map((file, index) => (
                          <div key={`${file.name}-${index}`} className="project-form__selection-row">
                            <div className="project-form__selection-copy">
                              <span className="project-form__selection-name">
                                {file.name}
                              </span>
                              <span className="project-form__selection-meta">
                                {(file.size / 1024).toFixed(1)} KB
                              </span>
                            </div>
                            <button
                              type="button"
                              className="project-form__selection-remove"
                              onClick={() => handleRemoveFile(index)}
                            >
                              Remove
                            </button>
                          </div>
                        ))}
                      </div>
                    ) : null}

                    <div className="project-form__tip">
                      <Lightbulb size={16} strokeWidth={2.1} />
                      <span>
                        {isPdfSubmission
                          ? 'PDF upload selected. Remove the file if you want to write essay text instead.'
                          : 'You do not need to add both text and files. Submit whichever format you prefer.'}
                      </span>
                    </div>
                  </>
                ) : null}
              </section>

              {!isPdfSubmission ? (
                <section className="project-form-page__card project-form-page__card--essay">
                  <SectionHeading
                    icon={FileText}
                    title={isEditing ? 'Submission Content' : 'Write Essay Text'}
                    optionalLabel="(Optional)"
                  />

                  {isEditing ? (
                    <div className="project-form__empty-panel">
                      <div className="project-form__empty-copy">
                        <Sparkles size={20} strokeWidth={2.1} />
                        <p>
                          Text and file submissions are managed from the project detail
                          page so iteration history stays intact.
                        </p>
                      </div>
                      <p className="project-form__helper">
                        {hasSubmissions
                          ? 'This project already has submissions. Open the detail page to submit a new version.'
                          : 'Open the project detail page when you are ready to submit content.'}
                      </p>
                      {projectId ? (
                        <a
                          className="project-form__inline-link"
                          href={`/projects/${projectId}`}
                        >
                          Open project detail page
                        </a>
                      ) : null}
                    </div>
                  ) : (
                    <>
                      <p className="project-form__helper">
                        Write your essay directly here, or upload a PDF on the left
                        instead.
                      </p>
                      <TextArea
                        value={formData.text}
                        onChange={(value) => handleInputChange('text', value)}
                        placeholder={
                          'Type your essay here...\n\nYou can paste or write up to 5,000 characters. The text will be analysed using the InterQ framework.'
                        }
                        rows={14}
                        maxLength={5000}
                        showCharCount={true}
                        resize="vertical"
                        className="project-form__essay-textarea"
                      />
                    </>
                  )}
                </section>
              ) : null}
            </div>
          </div>
        </div>

        <footer className="project-form-page__footer">
          <div className="project-form-page__footer-inner">
            <button
              type="button"
              className="project-form__button project-form__button--secondary"
              onClick={onCancel}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="project-form__button project-form__button--primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Submitting...' : isEditing ? 'Update' : 'Submit'}
            </button>
          </div>
        </footer>
      </form>

      <AlertDialog
        isOpen={isAlertOpen}
        type={alertType}
        title={alertTitle}
        message={alertMsg}
        primaryButtonText={primaryButtonText}
        onPrimaryAction={() => {
          if (alertType === 'info') {
            handleConfirmSubmit();
            return;
          }

          setIsAlertOpen(false);
          setAlertType('info');
          setAlertTitle(isEditing ? 'Review your changes' : 'Review your submission');
          setPrimaryButtonText(isEditing ? 'Update' : 'Create');
        }}
        onSecondaryAction={() => setIsAlertOpen(false)}
        showCloseButton
        closeOnOverlayClick
      />
    </div>
  );
};

export { ProjectForm };
export default ProjectForm;
